import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { backendConfig } from '../config/backendConfig';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';

export default function MentorSetup() {
  const navigate = useNavigate();
  const [seatsPurchased, setSeatsPurchased] = useState(0);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [emails, setEmails] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { orgCode, codes, invited }

  const seatsRemaining = useMemo(() => Math.max(0, seatsPurchased - seatsUsed), [seatsPurchased, seatsUsed]);

  useEffect(() => {
    const load = async () => {
      try {
        const uid = auth?.currentUser?.uid;
        if (!uid) { navigate('/login'); return; }
        const snap = await getDoc(doc(db, 'users', uid));
        const data = snap.exists() ? snap.data() : {};
        setSeatsPurchased(Number(data.mentorSeatsPurchased || 0));
        setSeatsUsed(Number(data.mentorSeatsUsed || 0));
        const count = Number(data.mentorSeatsPurchased || 0) - Number(data.mentorSeatsUsed || 0);
        setEmails(Array.from({ length: Math.max(1, count) }, () => ''));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const setEmailAt = (idx, val) => {
    setEmails(prev => prev.map((e, i) => (i === idx ? val : e)));
  };

  const addMentors = async () => {
    try {
      setError('');
      const uid = auth?.currentUser?.uid;
      if (!uid) { navigate('/login'); return; }
      const clean = emails.map(e => (e || '').trim().toLowerCase()).filter(Boolean);
      if (!clean.length) { setError('Please enter at least one mentor email'); return; }
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalid = clean.filter(e => !emailRe.test(e));
      if (invalid.length) { setError(`Invalid email: ${invalid[0]}`); return; }
      const unique = Array.from(new Set(clean));
      if (unique.length !== clean.length) { setError('Duplicate emails found. Please remove duplicates.'); return; }
      if (unique.length > seatsRemaining) { setError(`You can add up to ${seatsRemaining} mentors`); return; }

      // Trigger backend to validate seats, create invites, update seatsUsed, and send emails
      try {
        const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ownerId: uid, emails: unique })
        });
        const j = await resp.json().catch(()=>({}));
        if (resp && resp.ok) {
          setResult({ invited: j.invited, orgCode: j.orgCode, codes: j.codes });
        } else {
          setError(j.error || `Failed to send invites (HTTP ${resp.status})`);
        }
      } catch (_) {}

      // Keep user on this screen to show the summary modal instead of immediate navigation
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-to-br from-amber-900/85 via-orange-900/75 to-slate-900/85 backdrop-blur-sm">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-amber-300 text-amber-900">Loading…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFF8E1]">
      {/* Header with Logo and Navigation */}
      <ArtmosphereNavbar title="Artmosphere" subtitle="Mentor Setup" />
      
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-amber-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-200 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-amber-700">Mentor Seats</div>
              <h1 className="text-2xl font-extrabold text-amber-900">Add Mentor Logins</h1>
            </div>
            <button onClick={()=>navigate('/mentors')} className="text-amber-700 hover:text-amber-900 text-xl leading-none">×</button>
          </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="text-sm text-amber-800 mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 bg-amber-50 border border-amber-200">
            <span className="font-semibold">Seats:</span> {seatsUsed}/{seatsPurchased} used • <span className="font-semibold">Remaining:</span> {seatsRemaining}
          </div>

          {/* Scrollable email list area to fit many emails on smaller screens */}
          <div className="space-y-3 max-h-[40vh] overflow-auto pr-1">
            {emails.map((v, i) => (
              <div key={i} className="">
                <label className="block text-xs font-semibold text-amber-700 mb-1">Mentor email #{i+1}</label>
                <input type="email" value={v} onChange={(e)=>setEmailAt(i, e.target.value)} placeholder={`mentor${i+1}@school.org`} className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-400 bg-white/90" />
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button disabled={emails.length >= seatsRemaining} onClick={()=>setEmails(prev=>[...prev, ''])} className="px-4 py-2 rounded-xl border border-amber-200 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-50">Add another</button>
            <button disabled={emails.length <= 1} onClick={()=>setEmails(prev=>prev.slice(0, -1))} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50">Remove</button>
          </div>

          {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-amber-200 bg-white/90 flex items-center justify-end gap-2">
          <button onClick={()=>navigate('/mentors')} className="px-4 py-2 rounded-xl border border-amber-200 text-amber-800 bg-white hover:bg-amber-50">Cancel</button>
          <button disabled={submitting} onClick={async()=>{ setSubmitting(true); await addMentors(); setSubmitting(false); }} className="px-5 py-2 rounded-xl text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 font-semibold disabled:opacity-60">
            {submitting ? 'Sending invites…' : 'Confirm & Send Invites'}
          </button>
        </div>
      </div>
      {/* Success Summary Modal */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-amber-900/85 via-orange-900/75 to-slate-900/85 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-amber-300 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-300 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-amber-700">Invites Sent</div>
                <h2 className="text-2xl font-extrabold text-amber-900">Mentors Corner Ready</h2>
              </div>
              <button onClick={()=>setResult(null)} className="text-amber-700 hover:text-amber-900 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="text-sm text-amber-900">We've emailed {result.invited} mentor{result.invited>1?'s':''} with their access details. Share these codes if needed:</div>
              <div className="p-4 rounded-xl border border-amber-300 bg-amber-50">
                <div className="text-xs font-semibold text-amber-700 mb-1">Co‑Learning Space (Organization)</div>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-1 rounded bg-white border border-amber-300 text-amber-900">{result.orgCode}</code>
                  <button onClick={()=>navigator.clipboard.writeText(result.orgCode)} className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700">Copy</button>
                </div>
              </div>
              <div className="p-4 rounded-xl border border-amber-300 bg-amber-50">
                <div className="text-xs font-semibold text-amber-700 mb-2">Mentor Login Codes (Personal)</div>
                <div className="max-h-[30vh] overflow-auto pr-1 space-y-2">
                  {Object.entries(result.codes || {}).map(([email, code]) => (
                    <div key={email} className="flex items-center justify-between gap-2">
                      <div className="text-sm text-amber-900 truncate">{email}</div>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 rounded bg-white border border-amber-300 text-amber-900">{code}</code>
                        <button onClick={()=>navigator.clipboard.writeText(code)} className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700">Copy</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-xs text-amber-800">
                Mentors Corner gives access to project ideas and tools to guide students. The Co‑Learning Space helps your organization learn from each other's work.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-amber-300 bg-white/90 flex items-center justify-end gap-2">
              <button onClick={()=>{ setResult(null); navigate('/mentor'); }} className="px-4 py-2 rounded-xl border border-amber-300 text-amber-800 bg-white hover:bg-amber-50">Go to Mentor Dashboard</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


